import React, { useContext, useEffect, useMemo, useState } from 'react';
import AppContext from '../../../Context/AppContext';
import requestAxios from '../../../util/requestAxios';
import '../portalV2Shared.css';
import './ModalTimeZoneV2.css';

/**
 * Portal V2 timezone gate — same APIs as classic ModalTimeZone,
 * glass/gold UI so it matches Customize / Login V2.
 */
const ModalTimeZoneV2 = ({ open, onOpenChange }) => {
    const { checkUserSession, updateUserStepId } = useContext(AppContext);
    const [zones, setZones] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [required, setRequired] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError('');
            const userState = await updateUserStepId();
            const session = checkUserSession() || {};
            const { error: tzErr, data } = await requestAxios({
                url: 'customer/GetDefinedTimeZones',
                token: session.token,
            });
            if (cancelled) return;

            const preview = (() => {
                try {
                    return new URLSearchParams(window.location.search).get(
                        'preview'
                    );
                } catch {
                    return null;
                }
            })();

            const needsTz = userState?.definedTimeZoneId == null;
            setRequired(preview === 'tz' ? false : needsTz);

            if (preview === 'customize') {
                // Screenshot mode: keep timezone closed
            } else if (preview === 'tz' || needsTz) {
                if (onOpenChange) onOpenChange(true);
            }

            if (!tzErr && Array.isArray(data)) {
                const mapped = data.map((item) => {
                    const time = new Date(item.currentTime);
                    return {
                        id: String(item.id),
                        label: item.displayName,
                        clock: time.toLocaleTimeString('en-US'),
                    };
                });
                setZones(mapped);
                if (userState?.definedTimeZoneId != null) {
                    setSelectedId(String(userState.definedTimeZoneId));
                } else if (mapped.length) {
                    // Prefer a sensible default if browser offset matches a zone label
                    setSelectedId(mapped[0].id);
                }
            }
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selected = useMemo(
        () => zones.find((z) => z.id === selectedId) || null,
        [zones, selectedId]
    );

    if (!open) return null;

    const save = async () => {
        if (!selectedId) {
            setError('Choose a timezone to continue.');
            return;
        }
        const session = checkUserSession() || {};
        setBusy(true);
        setError('');
        const { error: saveErr } = await requestAxios({
            url: 'customer/UpdateUserDefinedTimeZone',
            method: 'put',
            token: session.token,
            data: {
                userId: session.userId,
                definedTimeZoneId: Number(selectedId),
            },
        });
        setBusy(false);
        if (saveErr) {
            setError('Could not save your timezone. Please try again.');
            return;
        }
        await updateUserStepId();
        if (onOpenChange) onOpenChange(false);
    };

    return (
        <div className="pv2-tz" role="dialog" aria-modal="true">
            <div className="pv2-tz__backdrop" />
            <div className="pv2-tz__card pv2-glass pv2-glass--strong pv2-anim">
                <p className="pv2-eyebrow">Almost there</p>
                <h2 className="pv2-tz__title">Select your timezone</h2>
                <p className="pv2-tz__sub">
                    We use this so your daily actions unlock at the right time
                    for you.
                </p>

                {loading ? (
                    <p className="pv2-sub">Loading timezones…</p>
                ) : (
                    <>
                        <label className="pv2-tz__field">
                            <span>Timezone</span>
                            <select
                                className="pv2-input pv2-tz__select"
                                value={selectedId}
                                onChange={(e) => setSelectedId(e.target.value)}
                                disabled={busy}
                            >
                                <option value="" disabled>
                                    Choose your timezone
                                </option>
                                {zones.map((z) => (
                                    <option key={z.id} value={z.id}>
                                        {z.label}
                                    </option>
                                ))}
                            </select>
                        </label>

                        {selected ? (
                            <p className="pv2-tz__clock">
                                Local time now:{' '}
                                <strong>{selected.clock}</strong>
                            </p>
                        ) : null}
                    </>
                )}

                {error ? <p className="pv2-error">{error}</p> : null}

                <button
                    type="button"
                    className="pv2-btn pv2-btn--primary pv2-tz__save"
                    onClick={save}
                    disabled={busy || loading || !selectedId}
                >
                    {busy ? 'Saving…' : 'Continue →'}
                </button>

                {!required ? (
                    <button
                        type="button"
                        className="pv2-btn pv2-btn--ghost pv2-tz__skip"
                        onClick={() => onOpenChange && onOpenChange(false)}
                        disabled={busy}
                    >
                        Not now
                    </button>
                ) : null}
            </div>
        </div>
    );
};

export default ModalTimeZoneV2;
